/**
 * Pluggable game engine contract.
 *
 * Each game is a pure state machine parameterised on its `State` and `Move`
 * types. The engine has no idea how state reaches it — the host wires
 * moves into MLS ChatPayloads and replays them through `applyMove` on
 * receive. Anything not listed as a player is treated as a spectator.
 */
import type { UserId } from '@chat/protocol';
import type { ReactElement } from 'react';

export interface GameTerminal {
  readonly winner?: UserId;
  readonly draw?: true;
}

export interface GameComponentProps<State, Move> {
  readonly state: State;
  readonly self: UserId;
  readonly players: readonly UserId[];
  readonly onMove: (move: Move) => void;
  readonly disabled?: boolean;
}

export interface GameEngine<State, Move> {
  readonly id: string;
  readonly displayName: string;
  readonly minPlayers: number;
  readonly maxPlayers: number;
  initialState(players: readonly UserId[]): State;
  validateMove(state: State, move: Move, by: UserId): string | null;
  applyMove(state: State, move: Move, by: UserId): State;
  currentTurn(state: State): UserId | null;
  terminal(state: State): GameTerminal | null;
  isPlayer(state: State, user: UserId): boolean;
  Component: (props: GameComponentProps<State, Move>) => ReactElement;
}

export class GameRegistry {
  readonly #engines = new Map<string, GameEngine<unknown, unknown>>();

  register<S, M>(engine: GameEngine<S, M>): void {
    if (this.#engines.has(engine.id)) {
      throw new Error(`game engine "${engine.id}" already registered`);
    }
    this.#engines.set(engine.id, engine as GameEngine<unknown, unknown>);
  }

  get(id: string): GameEngine<unknown, unknown> | undefined {
    return this.#engines.get(id);
  }

  list(): readonly GameEngine<unknown, unknown>[] {
    return Array.from(this.#engines.values());
  }
}
